import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useI18n } from '../context/I18nContext';

/**
 * One-click "Save as PDF" button. Snapshots the DOM node referenced by
 * `targetRef` and downloads it as a PDF using html2pdf.js. The library is
 * imported dynamically so it only ships to the browser when a visitor actually
 * clicks the button (~350 KB gzipped, worth deferring).
 */
export default function SaveQuotePdf({ targetRef, filename = 'insurance-quote.pdf', label }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!targetRef?.current) return;
    setBusy(true);
    try {
      const mod = await import('html2pdf.js');
      const html2pdf = mod.default || mod;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(targetRef.current)
        .save();
    } catch (err) {
      console.error(err);
      alert('Could not create PDF: ' + (err?.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={save}
      disabled={busy}
      data-testid="save-pdf-btn"
      className="btn-outline !py-2 !px-4 text-sm disabled:opacity-60"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
      {busy
        ? t('pdf.generating', 'Generating…')
        : (label || t('pdf.save_as_pdf', 'Save as PDF'))}
    </button>
  );
}
